import { ROUTES } from "../../constants/routes";
import { Component, inject } from "@angular/core";
import { RouterLink } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { SITE } from "../../constants/site";
import { ICONS } from "../../constants/icons";
import {
  ATTESTATIONS,
  COMPLIANCE_FAQS,
  CONTROL_AREAS,
  RETENTION_SCHEDULE,
  SAFEGUARD_MAPPINGS,
  SECURITY_PACKAGE_ITEMS,
  SOC2_ROADMAP,
  SUBPROCESSORS
} from "../../constants/compliance";
import { breadcrumbSchema, faqSchema, webPageSchema } from "../../helpers/structured-data";

@Component({
  selector: "app-security",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./security.html",
  styleUrl: "./security.scss"
})
export class Security {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly site = SITE;

  readonly attestations = ATTESTATIONS;
  readonly controlAreas = CONTROL_AREAS;
  readonly safeguards = SAFEGUARD_MAPPINGS;
  readonly retention = RETENTION_SCHEDULE;
  readonly subprocessors = SUBPROCESSORS;
  readonly packageItems = SECURITY_PACKAGE_ITEMS;
  readonly roadmap = SOC2_ROADMAP;
  readonly faqs = COMPLIANCE_FAQS;

  /**
   * Shown at the top of the page so a reviewer knows the content is current.
   * Update this whenever a control described on this page changes.
   */
  readonly lastReviewed = "December 2025";

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  commitments = [
    "Data is encrypted in transit with TLS 1.2 or higher and at rest with AES-256",
    "Access follows least privilege, and administrative access requires multi factor authentication",
    "Records you submit are retained only as long as needed, then deleted on a defined schedule",
    "We never sell, resell or license the records you send us",
    "API keys are scoped per environment and can be rotated or revoked by you at any time",
    "Infrastructure is monitored, patched and scanned, with annual penetration testing",
    "All customer data is hosted in United States regions",
    "We complete customer security questionnaires and join vendor review calls"
  ];

  ngOnInit(): void {
    this.seo.apply({
      title: "Security, Compliance and GLBA Safeguards",
      description:
        "AES-256 encryption at rest, TLS 1.2+ in transit, short documented retention and a full FTC Safeguards Rule control mapping. Built for bank and enterprise vendor review.",
      path: "/security",
      keywords:
        "GLBA Safeguards Rule compliance, SOC 2, death verification security, deceased data encryption, data retention policy, vendor security review, 16 CFR Part 314, death data compliance",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Security and compliance", path: "/security" }
        ]),
        webPageSchema({
          name: "Security and compliance at VerifyDeceased",
          description:
            "How VerifyDeceased protects customer information with encryption, access control, short retention and an information security program aligned to the FTC Safeguards Rule.",
          path: "/security"
        }),
        faqSchema(this.faqs)
      ]
    });
  }
}
